import { useEffect, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { ProfileHeroBadge } from '@/components/common/ProfileHero';
import { ProfileLogo } from '@/components/common/ProfileLogo';
import { SectionEditButton } from '@/components/common/SectionEditButton';
import { PARTNER_COVER_BAND_ASPECT_CLASS } from '@/lib/constants/image-crop';
import { PROFILE_LAYOUT_STRINGS as L } from '@/lib/constants/profile-layout-strings';
import { trustedPartnerDetailPath } from '@/lib/constants/routes';
import type { PartnerProfileData } from '../../api/partner-profile.api';
import { PARTNER_PROFILE_STRINGS as S } from './partner-profile.strings';

interface PartnerProfileHeroProps {
  data: PartnerProfileData;
  entityId: string;
  /** Opens the logo and cover panel; omitted when read-only or before setup. */
  onEditImages?: () => void;
}

const CONTACT_METHOD = {
  email: S.CONTACT_PREFERENCE_EMAIL,
  scheduling: S.CONTACT_PREFERENCE_SCHEDULING,
} as const;

/**
 * Shares the public detail page's cover shape (FR-198, A-227), so a cover cropped
 * to the recommended size is shown whole here too. It used to be a fixed 160 px
 * band while the public page used a fixed 256 px one - two heights for the same
 * image, neither matching the crop.
 */
function CoverBand({ url }: { url?: string | null }) {
  const [failed, setFailed] = useState(false);
  useEffect(() => setFailed(false), [url]);
  if (!url || failed) {
    return <div aria-hidden="true" className={`bg-teal w-full ${PARTNER_COVER_BAND_ASPECT_CLASS}`} />;
  }
  return (
    <img
      src={url}
      alt=""
      className={`bg-teal w-full object-cover ${PARTNER_COVER_BAND_ASPECT_CLASS}`}
      onError={() => setFailed(true)}
    />
  );
}

function partnerActions(entityId: string, onEditImages?: () => void) {
  return (
    <div className="flex items-center gap-2 pt-4">
      <Link
        to={trustedPartnerDetailPath(entityId)}
        className="text-dkgreen hover:text-dkgreen-hover mr-2 text-sm font-medium"
      >
        {S.VIEW_PUBLIC_PROFILE}
      </Link>
      {onEditImages && <SectionEditButton section={S.IMAGES} onClick={onEditImages} />}
    </div>
  );
}

/**
 * Partner identity card (FR-176, approved Option D): the partner's own cover
 * photo across the top of a rounded card, the circular logo overlapping its
 * lower edge, then name, Featured badge and tagline, with View Public Profile
 * and the logo and cover Edit on the right.
 */
function PartnerIdentityCard({ data, entityId, onEditImages }: PartnerProfileHeroProps) {
  const name = data.businessName || S.UNTITLED_PARTNER;
  return (
    <header className="rounded-card shadow-card overflow-hidden bg-white">
      <CoverBand url={data.coverPhotoUrl} />
      <div className="flex flex-wrap items-start gap-6 px-7 pb-6">
        <div className="-mt-14 shrink-0">
          <ProfileLogo
            url={data.logoUrl}
            name={name}
            size={112}
            shape="circle"
            ring="light"
            tone="maroon"
          />
        </div>
        {/* Starts below the cover's edge, so the name never touches the photo. */}
        <div className="min-w-0 flex-[1_1_22.5rem] pt-4">
          <div className="flex flex-wrap items-center gap-2.5">
            <h1 className="text-navy font-sans text-[1.75rem] leading-9 font-semibold">{name}</h1>
            {data.isFeatured && (
              <ProfileHeroBadge tone="featured">{S.FEATURED_TRUSTED_PARTNER}</ProfileHeroBadge>
            )}
          </div>
          {data.tagline && <p className="text-input text-subtext mt-0.5">{data.tagline}</p>}
        </div>
        {data.id && partnerActions(entityId, onEditImages)}
      </div>
    </header>
  );
}

interface StatTileProps {
  label: string;
  value: ReactNode;
  note?: string;
  /** The dark navy tile the mockup uses for the one figure to stand out. */
  highlight?: boolean;
}

/** One number tile under the partner card (mockup `.tile`). An empty value shows "-". */
function StatTile({ label, value, note, highlight = false }: StatTileProps) {
  const isEmpty = value === undefined || value === null || value === '';
  return (
    <div
      className={`rounded-card px-6 py-[1.375rem] ${highlight ? 'bg-navy text-white' : 'text-navy shadow-card bg-white'}`}
    >
      <dt className={`text-[0.8125rem] ${highlight ? 'text-slate' : 'text-subtext'}`}>{label}</dt>
      <dd className="mt-1 text-[1.625rem] leading-8 font-semibold break-words">
        {isEmpty ? L.EMPTY_STAT : value}
      </dd>
      {note && (
        <p className={`mt-1 text-[0.8125rem] ${highlight ? 'text-green' : 'text-dkgreen'}`}>
          {note}
        </p>
      )}
    </div>
  );
}

function PartnerStatTiles({ data }: { data: PartnerProfileData }) {
  const services = data.tags?.length ?? 0;
  const categories = data.categoryIds?.length ?? 0;
  const method = data.contactPreference === 'scheduling' ? 'scheduling' : 'email';
  return (
    <dl className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
      {/* Only meaningful once a scheduling link is on file (FR-118). */}
      <StatTile
        label={S.SCHEDULING_CLICKS}
        value={data.schedulingUrl ? (data.schedulingClickCount ?? 0) : undefined}
        note={S.SCHEDULING_CLICKS_NOTE}
      />
      <StatTile
        label={S.EMAIL_CONTACT_CLICKS}
        value={data.id ? (data.emailContactClickCount ?? 0) : undefined}
        note={S.EMAIL_CONTACT_CLICKS_NOTE}
      />
      <StatTile label={S.SERVICES_LISTED} value={services} note={S.ACROSS_CATEGORIES(categories)} />
      <StatTile
        label={S.CONTACT_METHOD_STAT}
        value={data.id ? CONTACT_METHOD[method] : undefined}
        highlight
      />
    </dl>
  );
}

/**
 * Partner profile header (FR-176, approved Option D): the identity card with
 * the cover photo, then a row of number tiles - scheduling clicks, email
 * contact clicks, services listed and how members reach the partner. No
 * completion % (A-156).
 */
export function PartnerProfileHero(props: PartnerProfileHeroProps) {
  return (
    <div className="flex flex-col gap-6">
      <PartnerIdentityCard {...props} />
      <PartnerStatTiles data={props.data} />
    </div>
  );
}
